<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { computed } from 'vue';
  import { Checkbox } from '../Checkbox';
  import {
    checkboxGroupClass,
    checkboxGroupItemsClass,
  } from './CheckboxGroup.css.ts';
  import type {
    CheckboxGroupOption,
    CheckboxGroupProps,
  } from './CheckboxGroup.types';

  const { t } = useLocale('export');

  const props = withDefaults(defineProps<CheckboxGroupProps>(), {
    showSelectAll: true,
  });

  const emits = defineEmits<{
    'update:options': [options: CheckboxGroupOption[]];
  }>();

  const allSelectedState = computed(() => {
    return props.options.every((option) => option.selected);
  });

  const toggleAll = () => {
    const newValue = !allSelectedState.value;
    const updatedOptions = props.options.map((option) => ({
      ...option,
      selected: newValue,
    }));
    emits('update:options', updatedOptions);
  };

  const updateOption = (index: number, selected: boolean) => {
    const updatedOptions = [...props.options];
    updatedOptions[index] = { ...updatedOptions[index], selected };
    emits('update:options', updatedOptions);
  };
</script>

<template>
  <div :class="checkboxGroupClass">
    <Checkbox
      v-if="showSelectAll"
      :model-value="allSelectedState"
      :name="`${id || 'checkbox-group'}-all`"
      :value="t('modals.export.columns.all')"
      :data-test-id="`${testId}-select-all`"
      @update:modelValue="toggleAll"
    />
    <div :class="checkboxGroupItemsClass">
      <Checkbox
        v-for="(option, index) in options"
        :key="option.value"
        :model-value="option.selected"
        :value="option.label"
        :name="`${id || 'checkbox-group'}-${option.value}`"
        :data-test-id="`${testId}-${option.value}`"
        @update:modelValue="
          (selected: boolean) => updateOption(index, selected)
        "
      />
    </div>
  </div>
</template>
